const CONTACT_EMAIL = 'noreply@qmsofts.com';

/**
 * Public, unauthenticated, no login required — this is the whole point of
 * the page. Google Play's account-deletion policy requires both an in-app
 * path (Settings > Account > Delete my account, see ManageScreen.tsx on
 * mobile) AND a web-reachable one for someone who's already deleted the app,
 * forgotten their password, or never installed it. The email path here is
 * that fallback: a monitored inbox, not a stub — the same shape many small
 * apps use to satisfy this requirement, since the policy only requires a
 * clear way to *request* deletion, not that it be instant self-service.
 */
export function AccountDeletionPage() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-12 text-sm leading-relaxed text-foreground">
      <h1 className="font-display text-3xl">Delete Your Account</h1>

      <p className="mt-6">
        You can request deletion of your account and the personal data tied to it at any time, in either
        of two ways.
      </p>

      <section className="mt-8 rounded-2xl border border-border/60 bg-card p-5">
        <h2 className="font-display text-lg">Option 1 — In the app</h2>
        <p className="mt-1.5 text-muted-foreground">The fastest way, if you still have the app installed:</p>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5">
          <li>Open the Farmacia app and sign in</li>
          <li>
            Go to <strong>Manage → Account</strong>
          </li>
          <li>
            Scroll down and tap <strong>Delete my account</strong>
          </li>
          <li>Enter your password to confirm</li>
        </ol>
        <p className="mt-3 text-muted-foreground">
          This deletes your name, email, and login immediately — you're signed out right away, and the
          login no longer exists.
        </p>
      </section>

      <section className="mt-6 rounded-2xl border border-border/60 bg-card p-5">
        <h2 className="font-display text-lg">Option 2 — By email</h2>
        <p className="mt-1.5 text-muted-foreground">
          If you no longer have the app installed, or can't sign in, email us and we'll process the request
          manually:
        </p>
        <a
          href={`mailto:${CONTACT_EMAIL}?subject=Account deletion request`}
          className="mt-3 inline-block rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground"
        >
          {CONTACT_EMAIL}
        </a>
        <p className="mt-3 text-muted-foreground">Please include, so we can find and verify your account:</p>
        <ul className="mt-1.5 list-disc space-y-1 pl-5 text-muted-foreground">
          <li>The email address or phone number your account is registered under</li>
          <li>Your pharmacy's name</li>
        </ul>
        <p className="mt-3 text-muted-foreground">We aim to complete email requests within 5 business days.</p>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-lg">What gets deleted</h2>
        <p className="mt-1.5">Deleted permanently: your name, email address, password, and login.</p>
        <p className="mt-2">
          <strong>Kept:</strong> sales, billing, and inventory records your pharmacy is legally required to
          retain (the same way a paper ledger doesn't disappear when an employee leaves) — but with your
          personal login gone, these are no longer tied to an account you can access. If you're the only
          admin for your pharmacy, another staff member needs to be made an admin first, so the pharmacy
          isn't left without one.
        </p>
      </section>

      <p className="mt-10 border-t border-border/60 pt-4 text-xs text-muted-foreground">
        See the{' '}
        <a href="/privacy-policy" className="text-primary underline">
          Privacy Policy
        </a>{' '}
        for more on what's collected and how it's used.
      </p>
    </div>
  );
}